import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { format, addDays, differenceInMinutes, differenceInCalendarDays, isBefore, startOfDay } from 'date-fns';
import {
  CalendarIcon, Loader2, MapPin, Clock, Banknote, Coffee, ArrowLeft, ArrowRight, X,
  CheckCircle2, ChevronRight, CreditCard, AlertTriangle, Star,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { formatApiError } from '../../lib/apiError';
import { trackEvent } from '../../lib/analytics';

// Phase 4 UX (Feb 2026): Find Cover is now a focused 5-step wizard with a
// review screen and a role-specific success page. Every screen answers
// ONE question so a first-time manager can publish in under 30 seconds.
//
//   Step 1  Branch                (auto-preselected if only one saved)
//   Step 2  Date                  (Today / Tomorrow / Choose / Multiple)
//   Step 3  Time                  (Start / Finish / Break)
//   Step 4  Pay                   (Hourly rate OR Total shift payment)
//   Step 5  Additional info       (optional)
//   Review  branch, date, time, break, pay, notes
//   Success "Your cover request has been sent."
//
// Backend contract is unchanged — we still POST /shifts with the same
// payload shape as the legacy form; the hourly-rate mode simply computes
// the total on the client before submission.

const TOTAL_STEPS = 5;

// Module-scope presentational shell so React doesn't rebuild its subtree
// on every parent render — kept outside PostShiftPage per lint rule.
function StepShell({ step, title, sub, testId, children }) {
  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-6 sm:p-8" data-testid={testId}>
        <p className="text-sm font-semibold text-slate-500 mb-1">Step {step} of {TOTAL_STEPS}</p>
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1">{title}</h2>
        {sub && <p className="text-base text-slate-500 mb-6">{sub}</p>}
        <div>{children}</div>
      </CardContent>
    </Card>
  );
}

const QUICK_TIMES = [
  { start: '07:00', end: '13:00', label: 'Morning' },
  { start: '09:00', end: '17:00', label: 'Full day' },
  { start: '13:00', end: '19:00', label: 'Afternoon' },
];

function computeShiftHours(startHM, endHM, breakMinutes) {
  if (!startHM || !endHM) return 0;
  const [sh, sm] = startHM.split(':').map(Number);
  const [eh, em] = endHM.split(':').map(Number);
  const start = new Date(2000, 0, 1, sh, sm);
  const end = new Date(2000, 0, 1, eh, em);
  const mins = differenceInMinutes(end, start) - (breakMinutes || 0);
  return Math.max(0, mins / 60);
}

export default function PostShiftPage() {
  const { api } = useAuth();
  const navigate = useNavigate();

  // ---------------------------------------------------------------- state ---
  const [step, setStep] = useState(1); // 1..5, 6=review, 7=success
  const [publishedShift, setPublishedShift] = useState(null); // set after successful publish
  const [publishing, setPublishing] = useState(false);

  // Payment gate (unchanged from previous version)
  const [paymentModal, setPaymentModal] = useState({ open: false, code: null, message: '', reason: '' });
  const [redirecting, setRedirecting] = useState(false);
  const [autoChargeConfig, setAutoChargeConfig] = useState(null);

  // Saved branches
  const [savedBranches, setSavedBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');

  // Coverage
  //   dateMode: 'today' | 'tomorrow' | 'another' | 'multiple'
  const [dateMode, setDateMode] = useState('today');
  const [singleDate, setSingleDate] = useState(null);
  const [singleOpen, setSingleOpen] = useState(false);
  const [specificDates, setSpecificDates] = useState([]);
  const [multiOpen, setMultiOpen] = useState(false);

  // Form
  const [formData, setFormData] = useState({
    branchName: '',
    location: '',
    postcode: '',
    city: '',
    area: '',
    scheduledStartTime: '',
    breakDurationMinutes: 30,
    scheduledEndTime: '',
    notes: '',
    // iter98 — Post Office-specific requirements. Kept minimal per spec (3c).
    requiredPostOfficeRole: '',
    smartIdRequired: false,
    languageRequirement: '',
  });

  // iter98 — Post Office role dropdown vocab (fetched once).
  const [poRoles, setPoRoles] = useState([]);

  // Pay
  const [payMode, setPayMode] = useState('total'); // 'total' | 'hourly'
  const [payValue, setPayValue] = useState(''); // string for input control

  // ------------------------------------------------------------ effects ---
  useEffect(() => {
    let mounted = true;
    api.get('/payments/auto-charge-config').then(res => {
      if (mounted) setAutoChargeConfig(res.data);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [api]);

  // iter98 — Post Office role vocabulary for the requirements dropdown.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/meta/post-office-vocab');
        if (!cancelled) setPoRoles(data.roles || []);
      } catch { /* silent — dropdown will be empty */ }
    })();
    return () => { cancelled = true; };
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/manager/branches');
        const list = res.data?.branches || [];
        if (cancelled) return;
        setSavedBranches(list);
        // Phase 4 UX: if only one branch exists (or a default is set),
        // preselect it. The manager should never have to choose branches
        // unnecessarily.
        const def = list.find((b) => b.isDefault) || (list.length === 1 ? list[0] : null);
        if (def) applyBranchInternal(def);
      } catch { /* fields remain editable manually */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const applyBranchInternal = (b) => {
    setSelectedBranchId(b.id);
    setFormData((prev) => ({
      ...prev,
      branchName: b.branchName || '',
      location: b.location || '',
      postcode: b.postcode || '',
      city: b.city || '',
      area: b.area || '',
    }));
  };

  const applyBranch = (id) => {
    if (!id) { setSelectedBranchId(''); return; }
    const b = savedBranches.find((x) => x.id === id);
    if (b) applyBranchInternal(b);
  };

  // -------------------------------------------------------- derived values --
  const today = startOfDay(new Date());
  const disablePast = (d) => isBefore(d, today);

  const primaryDate = useMemo(() => {
    if (dateMode === 'today') return today;
    if (dateMode === 'tomorrow') return addDays(today, 1);
    if (dateMode === 'another') return singleDate;
    if (dateMode === 'multiple') {
      const sorted = [...specificDates].sort((a, b) => a - b);
      return sorted[0] || null;
    }
    return null;
  }, [dateMode, singleDate, specificDates, today]);

  const totalDays = useMemo(() => {
    if (dateMode === 'multiple') return specificDates.length;
    return primaryDate ? 1 : 0;
  }, [dateMode, primaryDate, specificDates]);

  const coverageType = dateMode === 'multiple' ? 'specific_dates' : 'single_day';

  const shiftHours = useMemo(
    () => computeShiftHours(formData.scheduledStartTime, formData.scheduledEndTime, formData.breakDurationMinutes),
    [formData.scheduledStartTime, formData.scheduledEndTime, formData.breakDurationMinutes]
  );

  const totalPayNumber = useMemo(() => {
    const v = parseFloat(payValue);
    if (!isFinite(v) || v <= 0) return 0;
    if (payMode === 'hourly') return +(v * shiftHours).toFixed(2);
    return v;
  }, [payMode, payValue, shiftHours]);

  const dateSummary = useMemo(() => {
    if (dateMode === 'multiple') {
      if (specificDates.length === 0) return 'No dates selected';
      const sorted = [...specificDates].sort((a, b) => a - b);
      if (sorted.length === 1) return format(sorted[0], 'EEE d MMM yyyy');
      return `${sorted.length} dates from ${format(sorted[0], 'd MMM')} to ${format(sorted[sorted.length - 1], 'd MMM')}`;
    }
    if (!primaryDate) return '—';
    return format(primaryDate, 'EEE d MMM yyyy');
  }, [dateMode, primaryDate, specificDates]);

  // ---------------------------------------------------- step validation --
  const stepErrors = useMemo(() => {
    if (step === 1) {
      if (!formData.branchName?.trim()) return 'Enter your branch name.';
      if (!formData.location?.trim()) return 'Enter the branch address.';
    }
    if (step === 2) {
      if (dateMode === 'another' && !singleDate) return 'Pick a date.';
      if (dateMode === 'multiple' && specificDates.length === 0) return 'Pick at least one date.';
    }
    if (step === 3) {
      if (!formData.scheduledStartTime) return 'Set a start time.';
      if (!formData.scheduledEndTime) return 'Set a finish time.';
      if (formData.scheduledEndTime <= formData.scheduledStartTime) return 'Finish time must be after start time.';
    }
    if (step === 4) {
      const v = parseFloat(payValue);
      if (!isFinite(v) || v <= 0) return payMode === 'hourly' ? 'Enter an hourly rate above 0.' : 'Enter a total payment above 0.';
    }
    return null;
  }, [step, formData, dateMode, singleDate, specificDates, payValue, payMode]);

  const goNext = () => {
    if (stepErrors) { toast.error(stepErrors); return; }
    setStep((s) => Math.min(s + 1, 6));
  };
  const goBack = () => setStep((s) => Math.max(s - 1, 1));

  const removeSpecificDate = (idx) => setSpecificDates(prev => prev.filter((_, i) => i !== idx));

  const addSpecificDate = (d) => {
    if (!d) return;
    const dateStr = format(d, 'yyyy-MM-dd');
    if (specificDates.some((x) => format(x, 'yyyy-MM-dd') === dateStr)) {
      toast.error('Date already selected'); return;
    }
    setSpecificDates(prev => [...prev, d]);
    setMultiOpen(false);
  };

  // ---------------------------------------------------- payment gate ---
  const openSetupCard = useCallback(async () => {
    setRedirecting(true);
    try {
      const res = await api.post('/payments/setup-card', { originUrl: window.location.origin });
      if (res.data?.url) { window.location.href = res.data.url; return; }
      toast.error('Could not start card setup');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start card setup');
    } finally { setRedirecting(false); }
  }, [api]);

  // ---------------------------------------------------- publish -------
  const publish = async () => {
    // Pre-flight payment gate
    if (autoChargeConfig && !autoChargeConfig.hasPaymentMethod) {
      setPaymentModal({ open: true, code: 'PAYMENT_METHOD_REQUIRED', message: 'Add a payment method before posting your first shift.', reason: '' });
      return;
    }
    if (autoChargeConfig && autoChargeConfig.paymentLocked) {
      setPaymentModal({ open: true, code: 'PAYMENT_LOCKED', message: 'Your account is locked due to a failed payment.', reason: autoChargeConfig.paymentLockedReason || 'Please update your card to continue.' });
      return;
    }

    if (!primaryDate) { toast.error('Please pick a date.'); return; }
    if (totalPayNumber <= 0) { toast.error('Pay must be greater than 0.'); return; }

    setPublishing(true);
    try {
      const payload = {
        ...formData,
        date: format(primaryDate, 'yyyy-MM-dd'),
        pay: totalPayNumber,
        breakDurationMinutes: parseInt(formData.breakDurationMinutes) || 0,
        coverageType,
        coverageDays: totalDays,
        savedBranchId: selectedBranchId || null,
      };
      if (coverageType === 'specific_dates') {
        payload.selectedDates = [...specificDates].sort((a, b) => a - b).map(d => format(d, 'yyyy-MM-dd'));
      }
      const res = await api.post('/shifts', payload);
      try { trackEvent('first_shift_created', { coverageType, branchName: formData.branchName || null }); } catch { /* analytics only */ }
      // Move to success screen — keep the shift record so we can deep-link
      // the "View this shift" action.
      setPublishedShift(res.data || { id: null, ...payload });
      setStep(7);
    } catch (error) {
      // Existing sophisticated error handling — unchanged.
      if (!error.response) {
        toast.error('Network issue — please check your connection and try again.', { duration: 6000 });
        return;
      }
      const status = error.response.status;
      const detail = error.response.data?.detail;
      if (status === 402 && detail && typeof detail === 'object' && (detail.code === 'PAYMENT_METHOD_REQUIRED' || detail.code === 'PAYMENT_LOCKED')) {
        setPaymentModal({ open: true, code: detail.code, message: detail.message || 'A payment method is required to post shifts.', reason: detail.reason || '' });
        return;
      }
      if (status === 403 && detail && typeof detail === 'object' && detail.code === 'ONBOARDING_INCOMPLETE') {
        toast.error(detail.message || 'Please finish onboarding before posting shifts.', { duration: 6000 });
        return;
      }
      if (status >= 500) {
        toast.error('Server error — we couldn\'t send the cover request right now. Please try again in a moment.', { duration: 6000 });
        return;
      }
      const human = formatApiError(error, 'Please check the form and try again.');
      const prefix = (status === 422 || Array.isArray(detail)) ? 'Missing or invalid field — ' : '';
      toast.error(`${prefix}${human}`, { duration: 6000 });
    } finally { setPublishing(false); }
  };

  const startAnother = () => {
    setPublishedShift(null);
    setStep(1);
    setDateMode('today');
    setSingleDate(null);
    setSpecificDates([]);
    setPayValue('');
    setFormData(prev => ({ ...prev, scheduledStartTime: '', scheduledEndTime: '', notes: '' }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ==================================================== render ==========
  // SUCCESS SCREEN (step 7)
  if (step === 7 && publishedShift) {
    return (
      <div className="p-4 sm:p-6 max-w-2xl mx-auto" data-testid="find-cover-success">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="h-16 w-16 rounded-2xl bg-emerald-600 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-testid="success-title">
              Your cover request has been sent.
            </h2>
            <p className="text-base text-slate-600 mb-7">
              We&apos;ll let you know when workers respond.
            </p>
            <div className="space-y-3">
              {publishedShift.id && (
                <Button
                  onClick={() => navigate(`/manager/logs/${publishedShift.id}`)}
                  className="w-full h-14 rounded-full bg-[#0f1b3d] hover:bg-[#1a2d5a] text-white text-base font-semibold"
                  data-testid="success-view-shift-btn"
                >
                  View this shift <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              )}
              <Button
                onClick={startAnother}
                variant="outline"
                className="w-full h-14 rounded-full border-slate-300 text-base font-semibold"
                data-testid="success-find-another-btn"
              >
                Find cover for another shift
              </Button>
              <Button
                onClick={() => navigate('/manager')}
                variant="ghost"
                className="w-full h-12 rounded-full text-slate-500 hover:text-slate-800"
                data-testid="success-home-btn"
              >
                Back to home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto" data-testid="find-cover-page">
      {/* Header */}
      <div className="mb-5 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full h-11 w-11 min-w-[44px] shrink-0"
          onClick={() => (step === 1 || step === 6) ? navigate('/manager') : goBack()}
          data-testid="find-cover-back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']">Find Cover</h1>
          <p className="text-sm text-slate-500">Post a shift to find someone to cover it.</p>
        </div>
      </div>

      {/* Progress bar (steps 1..5). Hidden on review. */}
      {step <= TOTAL_STEPS && (
        <div className="flex gap-1.5 mb-5" data-testid="find-cover-progress">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full ${i < step ? 'bg-[#DA291C]' : 'bg-slate-200'}`}
            />
          ))}
        </div>
      )}

      {/* Payment lock banner */}
      {autoChargeConfig?.paymentLocked && (
        <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="payment-locked-banner">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-800">Account locked — payment failed</p>
            <p className="text-xs text-red-700 mt-0.5">{autoChargeConfig.paymentLockedReason || 'A previous shift charge failed. Update your card to resume posting.'}</p>
          </div>
          <Button size="sm" className="rounded-full bg-red-600 hover:bg-red-700 text-white" onClick={openSetupCard} disabled={redirecting} data-testid="banner-update-card-btn">
            {redirecting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Update card'}
          </Button>
        </div>
      )}

      {/* STEP 1 — Branch */}
      {step === 1 && (
        <StepShell
          step={step}
          title="Which branch?"
          sub={savedBranches.length > 1 ? 'Choose a saved branch or edit the details.' : 'The address workers will see.'}
          testId="step-branch"
        >
          {savedBranches.length > 1 && (
            <div className="mb-5" data-testid="branch-picker">
              <div className="flex flex-wrap gap-2">
                {savedBranches.map((b) => {
                  const active = selectedBranchId === b.id;
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => applyBranch(b.id)}
                      className={cn(
                        'min-h-[48px] px-4 rounded-full text-sm font-medium border-2 inline-flex items-center gap-1.5 transition-colors',
                        active ? 'bg-[#0f1b3d] text-white border-[#0f1b3d]' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                      )}
                      data-testid={`branch-pill-${b.id}`}
                    >
                      {b.isDefault && <Star className="h-3.5 w-3.5 fill-current" />}
                      {b.label || b.branchName}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <Label htmlFor="branchName" className="text-base font-medium text-slate-800 flex items-center gap-1.5 mb-1.5">
                <MapPin className="h-4 w-4 text-slate-500" /> Branch name
              </Label>
              <Input
                id="branchName"
                value={formData.branchName}
                onChange={(e) => setFormData(f => ({ ...f, branchName: e.target.value }))}
                placeholder="e.g., Central Post Office"
                className="h-12 rounded-xl text-base"
                data-testid="branch-name-input"
              />
            </div>
            <div>
              <Label htmlFor="location" className="text-base font-medium text-slate-800 mb-1.5 block">Branch address</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData(f => ({ ...f, location: e.target.value }))}
                placeholder="e.g., 123 High Street, London"
                className="h-12 rounded-xl text-base"
                data-testid="location-input"
              />
            </div>
            <div>
              <Label htmlFor="postcode" className="text-base font-medium text-slate-800 mb-1.5 block">Postcode</Label>
              <Input
                id="postcode"
                value={formData.postcode}
                onChange={(e) => setFormData(f => ({ ...f, postcode: e.target.value.toUpperCase() }))}
                placeholder="e.g., NW1 8QS"
                className="h-12 rounded-xl text-base uppercase"
                data-testid="postcode-input"
              />
            </div>
          </div>
        </StepShell>
      )}

      {/* STEP 2 — Date */}
      {step === 2 && (
        <StepShell step={step} title="When do you need cover?" sub="Pick a day, or add multiple." testId="step-date">
          <div className="grid grid-cols-2 gap-2" data-testid="date-mode-group">
            {[
              { v: 'today', label: 'Today', sub: format(today, 'EEE d MMM') },
              { v: 'tomorrow', label: 'Tomorrow', sub: format(addDays(today, 1), 'EEE d MMM') },
              { v: 'another', label: 'Choose another date' },
              { v: 'multiple', label: 'Multiple dates' },
            ].map((opt) => {
              const active = dateMode === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setDateMode(opt.v)}
                  className={cn(
                    'min-h-[64px] p-3 rounded-xl border-2 text-left transition-all',
                    active ? 'border-[#DA291C] bg-red-50 text-[#DA291C]' : 'border-slate-200 hover:border-slate-300 bg-white text-slate-800'
                  )}
                  data-testid={`date-mode-${opt.v}`}
                  aria-pressed={active}
                >
                  <p className="text-base font-semibold">{opt.label}</p>
                  {opt.sub && <p className="text-sm mt-0.5 opacity-80">{opt.sub}</p>}
                </button>
              );
            })}
          </div>

          {/* Reveal the calendar only when the manager asks for it. */}
          {dateMode === 'another' && (
            <div className="mt-5" data-testid="date-another-picker">
              <Popover open={singleOpen} onOpenChange={setSingleOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn('w-full h-12 rounded-xl justify-start text-base', !singleDate && 'text-slate-400')}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {singleDate ? format(singleDate, 'EEE d MMM yyyy') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={singleDate} onSelect={(d) => { setSingleDate(d); setSingleOpen(false); }} disabled={disablePast} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
          )}
          {dateMode === 'multiple' && (
            <div className="mt-5 space-y-3" data-testid="date-multiple-picker">
              <Popover open={multiOpen} onOpenChange={setMultiOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full h-12 rounded-xl justify-start text-base text-slate-500">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    Tap to add a date…
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={null} onSelect={addSpecificDate} disabled={disablePast} initialFocus />
                </PopoverContent>
              </Popover>
              {specificDates.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="specific-dates-chips">
                  {[...specificDates].sort((a, b) => a - b).map((d, idx) => (
                    <Badge key={format(d, 'yyyy-MM-dd')} className="bg-[#0f1b3d] text-white pl-3 pr-1.5 py-1.5 text-sm rounded-full">
                      {format(d, 'd MMM')}
                      <button type="button" onClick={() => removeSpecificDate(idx)} className="ml-1.5 h-5 w-5 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </StepShell>
      )}

      {/* STEP 3 — Time */}
      {step === 3 && (
        <StepShell step={step} title="What time?" sub="Set the shift start, finish and any break." testId="step-time">
          {/* Preset quick-times */}
          <div className="flex flex-wrap gap-2 mb-5" data-testid="time-presets">
            {QUICK_TIMES.map((p) => {
              const active = formData.scheduledStartTime === p.start && formData.scheduledEndTime === p.end;
              return (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => setFormData(f => ({ ...f, scheduledStartTime: p.start, scheduledEndTime: p.end }))}
                  className={cn(
                    'min-h-[44px] px-3.5 rounded-full text-sm font-medium border-2 transition-colors',
                    active ? 'bg-[#0f1b3d] text-white border-[#0f1b3d]' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                  )}
                  data-testid={`time-preset-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {p.label} · {p.start}–{p.end}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="start" className="text-base font-medium text-slate-800 flex items-center gap-1.5 mb-1.5">
                <Clock className="h-4 w-4 text-slate-500" /> Start time
              </Label>
              <Input id="start" type="time" value={formData.scheduledStartTime} onChange={(e) => setFormData(f => ({ ...f, scheduledStartTime: e.target.value }))} className="h-12 rounded-xl text-base" data-testid="start-time-input" />
            </div>
            <div>
              <Label htmlFor="end" className="text-base font-medium text-slate-800 mb-1.5 block">Finish time</Label>
              <Input id="end" type="time" value={formData.scheduledEndTime} onChange={(e) => setFormData(f => ({ ...f, scheduledEndTime: e.target.value }))} className="h-12 rounded-xl text-base" data-testid="end-time-input" />
            </div>
          </div>
          <div className="mt-4">
            <Label htmlFor="break" className="text-base font-medium text-slate-800 flex items-center gap-1.5 mb-1.5">
              <Coffee className="h-4 w-4 text-slate-500" /> Break (minutes)
            </Label>
            <Input id="break" type="number" min="0" max="120" value={formData.breakDurationMinutes} onChange={(e) => setFormData(f => ({ ...f, breakDurationMinutes: e.target.value }))} className="h-12 rounded-xl text-base" data-testid="break-duration-input" />
          </div>
          {shiftHours > 0 && (
            <p className="mt-3 text-sm text-slate-500" data-testid="shift-hours-hint">
              That&apos;s {shiftHours.toFixed(shiftHours % 1 === 0 ? 0 : 1)} paid hour{shiftHours === 1 ? '' : 's'} after the break.
            </p>
          )}
        </StepShell>
      )}

      {/* STEP 4 — Pay */}
      {step === 4 && (
        <StepShell step={step} title="How is the worker paid?" sub="Choose one — the worker will see the total on their app." testId="step-pay">
          <div className="grid grid-cols-2 gap-2 mb-4" data-testid="pay-mode-group">
            {[
              { v: 'total', label: 'Total shift payment', sub: 'e.g., £95.00 for the whole shift' },
              { v: 'hourly', label: 'Hourly rate', sub: 'e.g., £13.50 per hour' },
            ].map((opt) => {
              const active = payMode === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setPayMode(opt.v)}
                  className={cn(
                    'min-h-[80px] p-3.5 rounded-xl border-2 text-left transition-all',
                    active ? 'border-[#DA291C] bg-red-50' : 'border-slate-200 hover:border-slate-300 bg-white'
                  )}
                  data-testid={`pay-mode-${opt.v}`}
                  aria-pressed={active}
                >
                  <p className={cn('text-base font-semibold', active ? 'text-[#DA291C]' : 'text-slate-800')}>{opt.label}</p>
                  <p className="text-sm mt-0.5 text-slate-500">{opt.sub}</p>
                </button>
              );
            })}
          </div>
          <div>
            <Label htmlFor="pay" className="text-base font-medium text-slate-800 flex items-center gap-1.5 mb-1.5">
              <Banknote className="h-4 w-4 text-slate-500" />
              {payMode === 'hourly' ? 'Hourly rate (£)' : 'Total shift payment (£)'}
            </Label>
            <Input
              id="pay"
              type="number"
              min="0"
              step="0.01"
              placeholder={payMode === 'hourly' ? 'e.g., 13.50' : 'e.g., 95.00'}
              value={payValue}
              onChange={(e) => setPayValue(e.target.value)}
              className="h-12 rounded-xl text-base"
              data-testid="pay-input"
              inputMode="decimal"
            />
            {payMode === 'hourly' && shiftHours > 0 && totalPayNumber > 0 && (
              <p className="mt-2 text-sm text-slate-600" data-testid="hourly-total-preview">
                Worker will see £{totalPayNumber.toFixed(2)} total for {shiftHours.toFixed(shiftHours % 1 === 0 ? 0 : 1)} paid hour{shiftHours === 1 ? '' : 's'}.
              </p>
            )}
            {payMode === 'hourly' && shiftHours === 0 && (
              <p className="mt-2 text-sm text-amber-600">Set the shift times first so we can compute the total.</p>
            )}
          </div>
        </StepShell>
      )}

      {/* STEP 5 — Additional info + Post Office requirements */}
      {step === 5 && (
        <StepShell step={step} title="Anything the worker should know?" sub="Optional — parking, who to report to, specific experience, etc." testId="step-notes">
          {/* iter98 — Post Office requirements block. Kept compact so the
              Post Shift form stays fast per pilot spec (3c). Everything
              here is optional; leaving it blank preserves the previous
              behaviour. */}
          <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4" data-testid="post-office-requirements">
            <p className="text-sm font-semibold text-slate-700">Post Office requirements <span className="font-normal text-slate-400">(optional)</span></p>
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1 block">Required role</Label>
              <select
                value={formData.requiredPostOfficeRole}
                onChange={e => setFormData(f => ({ ...f, requiredPostOfficeRole: e.target.value }))}
                className="w-full h-11 rounded-xl border-2 border-slate-200 px-3 text-base bg-white"
                data-testid="req-po-role"
              >
                <option value="">Any Post Office role</option>
                {poRoles.map(r => (<option key={r} value={r}>{r}</option>))}
              </select>
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="req-smart-id-label">
              <input
                type="checkbox"
                checked={formData.smartIdRequired}
                onChange={e => setFormData(f => ({ ...f, smartIdRequired: e.target.checked }))}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-[#DA291C] focus:ring-[#DA291C]"
                data-testid="req-smart-id"
              />
              <div>
                <p className="text-sm font-semibold text-slate-800">Smart ID required</p>
                <p className="text-xs text-slate-500 mt-0.5">Only Smart ID-verified workers will be recommended for this shift.</p>
              </div>
            </label>
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1 block">
                Language requirement <span className="text-slate-400 font-normal">(only if genuinely needed)</span>
              </Label>
              <Input
                value={formData.languageRequirement}
                onChange={e => setFormData(f => ({ ...f, languageRequirement: e.target.value }))}
                placeholder="e.g., Polish"
                className="h-11 rounded-xl text-base"
                data-testid="req-language"
              />
            </div>
          </div>

          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData(f => ({ ...f, notes: e.target.value }))}
            placeholder={"e.g. Ring the buzzer on arrival. Ask for Priya."}
            rows={5}
            className="rounded-xl text-base"
            data-testid="notes-input"
          />
        </StepShell>
      )}

      {/* STEP 6 — Review */}
      {step === 6 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 sm:p-8" data-testid="step-review">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1">Review your cover request</h2>
            <p className="text-base text-slate-500 mb-5">Everything looks right? We&apos;ll send it to available workers.</p>
            <div className="rounded-xl border-2 border-slate-200 divide-y divide-slate-200 mb-6">
              {[
                { k: 'Branch', v: `${formData.branchName}${formData.location ? ` · ${formData.location}` : ''}`, edit: 1, testId: 'review-branch' },
                { k: 'Date', v: dateSummary, edit: 2, testId: 'review-date' },
                { k: 'Time', v: `${formData.scheduledStartTime}–${formData.scheduledEndTime}`, edit: 3, testId: 'review-time' },
                { k: 'Break', v: `${formData.breakDurationMinutes} minutes`, edit: 3, testId: 'review-break' },
                { k: 'Pay', v: payMode === 'hourly'
                    ? `£${parseFloat(payValue).toFixed(2)}/hour · £${totalPayNumber.toFixed(2)} total`
                    : `£${totalPayNumber.toFixed(2)} total`, edit: 4, testId: 'review-pay' },
                { k: 'Notes', v: formData.notes ? formData.notes : '—', edit: 5, testId: 'review-notes' },
              ].map((row) => (
                <div key={row.k} className="flex items-center justify-between gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-500 font-medium">{row.k}</p>
                    <p className="text-base text-slate-900 mt-0.5 whitespace-pre-line break-words" data-testid={row.testId}>{row.v}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep(row.edit)}
                    className="text-sm font-semibold text-[#DA291C] hover:underline shrink-0 min-h-[44px]"
                    data-testid={`edit-${row.testId}`}
                  >
                    Edit
                  </button>
                </div>
              ))}
            </div>
            <Button
              onClick={publish}
              disabled={publishing}
              className="w-full h-14 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white text-base font-semibold disabled:opacity-60"
              data-testid="publish-btn"
            >
              {publishing ? (<><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Sending…</>) : 'Send to available workers'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Footer — Next / Continue button for steps 1..5. Review has its own submit. */}
      {step >= 1 && step <= 5 && (
        <div className="mt-5 flex items-center gap-3">
          {step > 1 && (
            <Button variant="ghost" className="rounded-full h-12 px-5 text-slate-600 min-h-[48px]" onClick={goBack} data-testid="step-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
            </Button>
          )}
          <Button
            className="flex-1 h-14 rounded-full bg-[#0f1b3d] hover:bg-[#1a2d5a] text-white text-base font-semibold"
            onClick={step === 5 ? () => { if (stepErrors) { toast.error(stepErrors); return; } setStep(6); } : goNext}
            data-testid="step-next-btn"
          >
            {step === 5 ? 'Review' : 'Continue'} <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      )}

      {/* Payment Required / Locked Modal — unchanged */}
      <Dialog open={paymentModal.open} onOpenChange={(open) => { if (!open) setPaymentModal({ open: false, code: null, message: '', reason: '' }); }}>
        <DialogContent className="sm:max-w-md" data-testid="payment-required-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {paymentModal.code === 'PAYMENT_LOCKED' ? (<><AlertTriangle className="h-5 w-5 text-red-600" /> Account locked</>) : (<><CreditCard className="h-5 w-5 text-blue-600" /> Add a payment method</>)}
            </DialogTitle>
            <DialogDescription>{paymentModal.message}</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            {paymentModal.reason && (<div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="payment-modal-reason">{paymentModal.reason}</div>)}
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
              <p>Your Pilot Plan covers unlimited shift postings. No per-shift charge applies — billing is handled monthly by ShiftCover365.</p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="rounded-full" onClick={() => setPaymentModal({ open: false, code: null, message: '', reason: '' })} data-testid="payment-modal-cancel-btn">Not now</Button>
            <Button className="rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white" onClick={openSetupCard} disabled={redirecting} data-testid="payment-modal-add-card-btn">
              {redirecting ? (<><Loader2 className="h-4 w-4 animate-spin mr-2" /> Redirecting…</>) : paymentModal.code === 'PAYMENT_LOCKED' ? 'Update card' : 'Add card'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
